// frontend/src/components/admin/SuscripcionesSection.jsx
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAdminList, useModal, useConfirm } from "../../hooks/useAdmin";
import { Button } from "../shared/Button";
import { Input } from "../shared/Input";
import { Select } from "../shared/Select";
import { Card } from "../shared/Card";
import { Table } from "../shared/Table";
import { Badge } from "../shared/Badge";
import { Loading } from "../shared/Loading";
import { ErrorMessage } from "../shared/ErrorMessage";
import { Modal } from "../shared/Modal";
import { formatShortDate } from "../../utils/format";
import * as adminApi from "../../services/api";

function EstadoBadge({ row }) {
  if (!row.activo) {
    return <Badge variant="danger">Inactiva</Badge>;
  }
  if (row.esta_vencida) {
    return <Badge variant="warning">Vencida</Badge>;
  }
  const dias = row.dias_restantes ?? 0;
  const variant = dias <= 5 ? "warning" : "success";
  return <Badge variant={variant}>Activa ({dias}d)</Badge>;
}

function PagoBadge({ pago_pendiente }) {
  if (!pago_pendiente) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-300">
      $ Pago pendiente
    </span>
  );
}

export function SuscripcionesSection({ primaryColor = "#6366F1" }) {
  const [filterMode, setFilterMode] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionId, setActionId] = useState(null);
  const [globalSubmitting, setGlobalSubmitting] = useState(false);

  const fetchSuscripciones = async () => {
    if (filterMode === "pending") return await adminApi.listarSuscripciones(null, true);
    if (filterMode === "active")  return await adminApi.listarSuscripciones(true);
    if (filterMode === "inactive") return await adminApi.listarSuscripciones(false);
    return await adminApi.listarSuscripciones(null); // "all"
  };

  const { items: suscripciones, loading, error, reload } = useAdminList(fetchSuscripciones);
  const { items: usuarios } = useAdminList(adminApi.listarUsuarios);
  const { items: planes } = useAdminList(adminApi.listarPlanes);

  const createModal = useModal();
  const editModal = useModal();
  const creditosModal = useModal();
  const renovarModal = useModal();
  const { confirmAsync } = useConfirm();

  const [createFormData, setCreateFormData] = useState({ cliente_id: "", plan_id: "", pago_pendiente: false });
  const [editFormData, setEditFormData] = useState({ plan_id: "", fecha_inicio: "", fecha_fin: "", creditos_restantes: "" });
  const [creditosValue, setCreditosValue] = useState("");
  const [renovarPagoPendiente, setRenovarPagoPendiente] = useState(false);

  useEffect(() => { reload(); }, [filterMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const suscripcionesFiltradas = useMemo(() => {
    if (!searchTerm.trim()) return suscripciones;
    const term = searchTerm.toLowerCase();
    return suscripciones.filter((s) => {
      const nombreCliente = s.cliente?.nombre_completo?.toLowerCase() || "";
      const username = s.cliente?.username?.toLowerCase() || "";
      const nombrePlan = s.plan?.nombre?.toLowerCase() || "";
      const creditos = (s.creditos_restantes ?? "").toString();
      const inicio = formatShortDate(s.fecha_inicio).toLowerCase();
      const fin = formatShortDate(s.fecha_fin).toLowerCase();
      const estado = s.activo ? (s.esta_vencida ? "vencida" : "activa") : "inactiva";
      const pago = s.pago_pendiente ? "pendiente" : "";
      return (
        nombreCliente.includes(term) || username.includes(term) ||
        nombrePlan.includes(term) || creditos.includes(term) ||
        inicio.includes(term) || fin.includes(term) ||
        estado.includes(term) || pago.includes(term)
      );
    });
  }, [suscripciones, searchTerm]);

  const resultsLabel = useMemo(() => {
    if (!searchTerm.trim()) return `Total: ${suscripciones.length}`;
    if (suscripcionesFiltradas.length === 0) return "Sin resultados";
    if (suscripcionesFiltradas.length === suscripciones.length) return `Mostrando todas (${suscripciones.length})`;
    return `Mostrando ${suscripcionesFiltradas.length} de ${suscripciones.length}`;
  }, [searchTerm, suscripciones.length, suscripcionesFiltradas.length]);

  const btnBase = "inline-flex items-center justify-center rounded-xl border px-3 py-1.5 text-xs font-medium transition focus:outline-none focus:ring-2";
  const btnWarn = `${btnBase} border-yellow-500/30 bg-yellow-600/10 text-yellow-300 hover:bg-yellow-600/20`;
  const btnInfo = `${btnBase} border-blue-500/30 bg-blue-600/10 text-blue-300 hover:bg-blue-600/20`;
  const btnPurple = `${btnBase} border-purple-500/30 bg-purple-600/10 text-purple-300 hover:bg-purple-600/20`;
  const btnDanger = `${btnBase} border-red-500/30 bg-red-600/10 text-red-300 hover:bg-red-600/20`;
  const btnSuccess = `${btnBase} border-green-500/30 bg-green-600/10 text-green-300 hover:bg-green-600/20`;
  const btnAmber = `${btnBase} border-amber-500/30 bg-amber-600/10 text-amber-300 hover:bg-amber-600/20`;

  // ---------- Crear
  const handleCreateSubmit = async () => {
    if (!createFormData.cliente_id || !createFormData.plan_id) {
      toast.error("Cliente y plan son obligatorios", { icon: "⚠️" });
      return;
    }
    setGlobalSubmitting(true);
    const loadingToast = toast.loading("Creando suscripción...", { style: { borderLeft: `4px solid ${primaryColor}` } });
    try {
      const result = await adminApi.crearSuscripcion(createFormData);
      if (result?.error) {
        toast.error(result.error, { icon: "❌", duration: 5000 });
      } else {
        toast.success("Suscripción creada", { icon: "✅", duration: 2500 });
        createModal.close();
        setCreateFormData({ cliente_id: "", plan_id: "", pago_pendiente: false });
        reload();
      }
    } catch {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
      setGlobalSubmitting(false);
    }
  };

  // ---------- Editar
  const handleEditOpen = (suscripcion) => {
    editModal.open(suscripcion);
    setEditFormData({ plan_id: suscripcion.plan?.id ?? "", fecha_inicio: suscripcion.fecha_inicio ?? "", fecha_fin: suscripcion.fecha_fin ?? "", creditos_restantes: "" });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editModal.data) return;
    const payload = {};
    if (editFormData.plan_id && editFormData.plan_id !== editModal.data.plan?.id) payload.plan_id = editFormData.plan_id;
    if (editFormData.fecha_inicio && editFormData.fecha_inicio !== editModal.data.fecha_inicio) payload.fecha_inicio = editFormData.fecha_inicio;
    if (editFormData.fecha_fin && editFormData.fecha_fin !== editModal.data.fecha_fin) payload.fecha_fin = editFormData.fecha_fin;
    if (editFormData.creditos_restantes !== "") {
      const n = Number(editFormData.creditos_restantes);
      if (!Number.isFinite(n) || n < 0) { toast.error("Créditos inválidos", { icon: "⚠️" }); return; }
      payload.creditos_restantes = n;
    }
    if (Object.keys(payload).length === 0) { toast("No hay cambios para guardar", { icon: "ℹ️" }); return; }
    setGlobalSubmitting(true);
    const loadingToast = toast.loading("Guardando cambios...", { style: { borderLeft: `4px solid ${primaryColor}` } });
    try {
      const result = await adminApi.editarSuscripcion(editModal.data.id, payload);
      if (result?.error) {
        toast.error(result.error, { icon: "❌", duration: 5000 });
      } else {
        toast.success("Suscripción actualizada", { icon: "✅", duration: 2500 });
        editModal.close();
        reload();
      }
    } catch {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
      setGlobalSubmitting(false);
    }
  };

  // ---------- Créditos
  const handleCreditosOpen = (suscripcion) => { creditosModal.open(suscripcion); setCreditosValue(String(suscripcion.creditos_restantes ?? 0)); };

  const handleCreditosSubmit = async (e) => {
    e.preventDefault();
    if (!creditosModal.data) return;
    const creditos = Number(creditosValue);
    if (!Number.isFinite(creditos) || creditos < 0) { toast.error("Créditos inválidos", { icon: "⚠️" }); return; }
    setGlobalSubmitting(true);
    const loadingToast = toast.loading("Actualizando créditos...", { style: { borderLeft: `4px solid ${primaryColor}` } });
    try {
      const result = await adminApi.modificarCreditos(creditosModal.data.id, creditos);
      if (result?.error) {
        toast.error(result.error, { icon: "❌", duration: 5000 });
      } else {
        toast.success("Créditos actualizados", { icon: "✅", duration: 2500 });
        creditosModal.close();
        reload();
      }
    } catch {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
      setGlobalSubmitting(false);
    }
  };

  // ---------- Acciones por fila
  const withRowAction = async (id, label, fn) => {
    setActionId(id);
    const loadingToast = toast.loading(label, { style: { borderLeft: `4px solid ${primaryColor}` } });
    try {
      const result = await fn();
      if (result?.error) {
        toast.error(result.error, { icon: "❌", duration: 5000 });
      } else {
        toast.success("Listo", { icon: "✅", duration: 2000 });
        reload();
      }
    } catch {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
      setActionId(null);
    }
  };

  // ---------- Renovar (con modal para pago_pendiente)
  const handleRenovarOpen = (suscripcion) => {
    setRenovarPagoPendiente(false);
    renovarModal.open(suscripcion);
  };

  const handleRenovarSubmit = async () => {
    if (!renovarModal.data) return;
    renovarModal.close();
    await withRowAction(renovarModal.data.id, "Renovando suscripción...", () =>
      adminApi.renovarSuscripcion(renovarModal.data.id, { pago_pendiente: renovarPagoPendiente })
    );
  };

  const handleMarcarPagado = async (suscripcion) => {
    await withRowAction(suscripcion.id, "Confirmando pago...", () =>
      adminApi.marcarPagado(suscripcion.id)
    );
  };

  const handleDesactivar = async (suscripcion) => {
    const name = suscripcion.cliente?.nombre_completo || suscripcion.cliente?.username || "cliente";
    const confirmed = await confirmAsync(`¿Desactivar la suscripción de ${name}?`);
    if (!confirmed) return;
    await withRowAction(suscripcion.id, "Desactivando suscripción...", () => adminApi.desactivarSuscripcion(suscripcion.id));
  };

  const handleActivar = async (suscripcion) => {
    const name = suscripcion.cliente?.nombre_completo || suscripcion.cliente?.username || "cliente";
    const confirmed = await confirmAsync(`¿Activar la suscripción de ${name}?\n\nEsto desactivará otras suscripciones activas del mismo cliente.`);
    if (!confirmed) return;
    await withRowAction(suscripcion.id, "Activando suscripción...", () => adminApi.activarSuscripcion(suscripcion.id));
  };

  const handleEliminar = async (suscripcion) => {
    const name = suscripcion.cliente?.nombre_completo || suscripcion.cliente?.username || "cliente";
    const confirmed = await confirmAsync(`ELIMINAR permanentemente la suscripción de ${name}?\n\nEsta acción NO se puede deshacer.\n\nSolo se puede eliminar si está INACTIVA.`);
    if (!confirmed) return;
    await withRowAction(suscripcion.id, "Eliminando suscripción...", () => adminApi.eliminarSuscripcion(suscripcion.id));
  };

  // ---------- Columnas
  const columns = useMemo(() => [
    {
      key: "cliente",
      header: "Cliente",
      render: (cliente) => (
        <div className="flex flex-col">
          <span className="font-medium text-white">{cliente?.nombre_completo || cliente?.username}</span>
          <span className="text-xs text-slate-500">{cliente?.username}</span>
        </div>
      ),
    },
    {
      key: "plan",
      header: "Plan",
      render: (plan) => <span className="text-slate-200">{plan?.nombre}</span>,
    },
    {
      key: "fecha_inicio",
      header: "Inicio",
      render: (fecha) => formatShortDate(fecha),
    },
    {
      key: "fecha_fin",
      header: "Fin",
      render: (fecha) => formatShortDate(fecha),
    },
    {
      key: "creditos_restantes",
      header: "Créditos",
      align: "text-center",
      render: (creditos) => <span className="text-white font-semibold">{creditos ?? 0}</span>,
    },
    {
      key: "activo",
      header: "Estado",
      render: (_, row) => (
        <div className="flex flex-col gap-1">
          <EstadoBadge row={row} />
          <PagoBadge pago_pendiente={row.pago_pendiente} />
        </div>
      ),
    },
    {
      key: "actions",
      header: "Acciones",
      align: "text-center",
      render: (_, row) => {
        const busy = actionId === row.id;
        return (
          <div className="flex justify-center gap-2 flex-wrap">
            <button onClick={(e) => { e.stopPropagation(); handleEditOpen(row); }} className={btnWarn} style={{ "--tw-ring-color": primaryColor }} disabled={busy}>
              Editar
            </button>

            <button onClick={(e) => { e.stopPropagation(); handleRenovarOpen(row); }} className={btnPurple} style={{ "--tw-ring-color": primaryColor }} disabled={busy}>
              Renovar
            </button>

            <button onClick={(e) => { e.stopPropagation(); handleCreditosOpen(row); }} className={btnInfo} style={{ "--tw-ring-color": primaryColor }} disabled={busy}>
              Créditos
            </button>

            {row.pago_pendiente && (
              <button onClick={(e) => { e.stopPropagation(); handleMarcarPagado(row); }} className={btnAmber} style={{ "--tw-ring-color": primaryColor }} disabled={busy}>
                Cobrado ✓
              </button>
            )}

            {row.activo ? (
              <button onClick={(e) => { e.stopPropagation(); handleDesactivar(row); }} className={btnDanger} style={{ "--tw-ring-color": primaryColor }} disabled={busy}>
                Desactivar
              </button>
            ) : (
              <button onClick={(e) => { e.stopPropagation(); handleActivar(row); }} className={btnSuccess} style={{ "--tw-ring-color": primaryColor }} disabled={busy}>
                Activar
              </button>
            )}

            {!row.activo && (
              <button onClick={(e) => { e.stopPropagation(); handleEliminar(row); }} className={btnDanger} style={{ "--tw-ring-color": primaryColor }} disabled={busy}>
                Eliminar
              </button>
            )}
          </div>
        );
      },
    },
  ], [actionId, primaryColor]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && suscripciones.length === 0) return <Loading primaryColor={primaryColor} />;
  if (error) return <ErrorMessage message={error} onRetry={reload} />;

  return (
    <div className="space-y-4">
      <Card
        primaryColor={primaryColor}
        title="Suscripciones"
        subtitle="Creá, renová y administrá suscripciones"
        actions={
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              className="px-3 py-2 bg-slate-800/60 border border-slate-700 text-white rounded-xl w-full sm:w-auto text-sm focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": primaryColor }}
            >
              <option value="active">Activas</option>
              <option value="inactive">Inactivas</option>
              <option value="pending">$ Pago pendiente</option>
              <option value="all">Todas</option>
            </select>

            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-xs text-slate-400">{resultsLabel}</span>
              <Button variant="primary" onClick={() => createModal.open()} primaryColor={primaryColor} className="w-full sm:w-auto text-sm py-2">
                Nueva suscripción
              </Button>
            </div>
          </div>
        }
      >
        <div className="mb-5">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Buscar por cliente, plan, estado, pago pendiente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-3 rounded-xl bg-slate-800/40 border border-slate-700 text-white placeholder-slate-500 focus:ring-2 focus:border-transparent transition"
              style={{ "--tw-ring-color": primaryColor }}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm("")} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
          <div className="mt-2 text-xs text-slate-400">
            {searchTerm.trim() ? (suscripcionesFiltradas.length === 0 ? <span className="text-red-300">No se encontraron suscripciones</span> : <span>{resultsLabel}</span>) : <span>{resultsLabel}</span>}
          </div>
        </div>

        <Table rowKey="id" columns={columns} data={suscripcionesFiltradas} primaryColor={primaryColor} emptyMessage={searchTerm ? `No se encontraron suscripciones con "${searchTerm}"` : "No hay suscripciones"} />
      </Card>

      {/* MODAL: Crear */}
      <Modal isOpen={createModal.isOpen} onClose={createModal.close} title="Nueva suscripción" primaryColor={primaryColor}>
        <div className="space-y-4">
          <Select label="Cliente" name="cliente_id" value={createFormData.cliente_id} onChange={(name, value) => setCreateFormData((p) => ({ ...p, [name]: value }))} options={usuarios.map((u) => ({ value: u.id, label: `${u.username} - ${u.first_name} ${u.last_name}`.trim() }))} required primaryColor={primaryColor} placeholder="Seleccionar cliente" />
          <Select label="Plan" name="plan_id" value={createFormData.plan_id} onChange={(name, value) => setCreateFormData((p) => ({ ...p, [name]: value }))} options={planes.map((p) => ({ value: p.id, label: `${p.nombre} - ${p.creditos} créditos ($${p.precio})` }))} required primaryColor={primaryColor} placeholder="Seleccionar plan" />

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div className="relative">
              <input
                type="checkbox"
                className="sr-only"
                checked={createFormData.pago_pendiente}
                onChange={(e) => setCreateFormData((p) => ({ ...p, pago_pendiente: e.target.checked }))}
              />
              <div className={`w-10 h-5 rounded-full transition ${createFormData.pago_pendiente ? "bg-amber-500" : "bg-slate-700"}`} />
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${createFormData.pago_pendiente ? "translate-x-5" : ""}`} />
            </div>
            <span className="text-sm text-slate-300">
              Marcar como <span className="text-amber-300 font-medium">pago pendiente</span>
            </span>
          </label>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button variant="ghost" onClick={createModal.close} disabled={globalSubmitting}>Cancelar</Button>
            <Button variant="success" onClick={handleCreateSubmit} disabled={globalSubmitting}>
              {globalSubmitting ? "Creando..." : "Crear suscripción"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* MODAL: Editar */}
      <Modal isOpen={editModal.isOpen} onClose={editModal.close} title="Editar suscripción" primaryColor={primaryColor}>
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <Select label="Plan" name="plan_id" value={editFormData.plan_id} onChange={(name, value) => setEditFormData((p) => ({ ...p, [name]: value }))} options={planes.map((p) => ({ value: p.id, label: `${p.nombre} - ${p.creditos} créditos` }))} primaryColor={primaryColor} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Fecha inicio" name="fecha_inicio" type="date" value={editFormData.fecha_inicio} onChange={(name, value) => setEditFormData((p) => ({ ...p, [name]: value }))} primaryColor={primaryColor} />
            <Input label="Fecha fin" name="fecha_fin" type="date" value={editFormData.fecha_fin} onChange={(name, value) => setEditFormData((p) => ({ ...p, [name]: value }))} primaryColor={primaryColor} />
          </div>
          <Input label="Créditos restantes" name="creditos_restantes" type="number" value={editFormData.creditos_restantes} onChange={(name, value) => setEditFormData((p) => ({ ...p, [name]: value }))} placeholder={`Actual: ${editModal.data?.creditos_restantes ?? 0}`} min="0" primaryColor={primaryColor} />
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button variant="ghost" onClick={editModal.close} type="button" disabled={globalSubmitting}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={globalSubmitting} primaryColor={primaryColor}>
              {globalSubmitting ? "Guardando..." : "Guardar cambios"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* MODAL: Créditos */}
      <Modal isOpen={creditosModal.isOpen} onClose={creditosModal.close} title="Modificar créditos" primaryColor={primaryColor}>
        <form onSubmit={handleCreditosSubmit} className="space-y-4">
          <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4">
            <p className="text-xs text-slate-400">Cliente</p>
            <p className="text-white font-semibold">{creditosModal.data?.cliente?.nombre_completo || creditosModal.data?.cliente?.username}</p>
            <p className="text-xs text-slate-400 mt-3">Plan</p>
            <p className="text-white font-semibold">{creditosModal.data?.plan?.nombre}</p>
          </div>
          <Input label="Nuevos créditos" name="creditos" type="number" value={creditosValue} onChange={(_, value) => setCreditosValue(value)} min="0" required primaryColor={primaryColor} />
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button variant="ghost" onClick={creditosModal.close} type="button" disabled={globalSubmitting}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={globalSubmitting} primaryColor={primaryColor}>
              {globalSubmitting ? "Actualizando..." : "Actualizar créditos"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* MODAL: Renovar */}
      <Modal isOpen={renovarModal.isOpen} onClose={renovarModal.close} title="Renovar suscripción" primaryColor={primaryColor}>
        <div className="space-y-4">
          <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4">
            <p className="text-xs text-slate-400">Cliente</p>
            <p className="text-white font-semibold">{renovarModal.data?.cliente?.nombre_completo || renovarModal.data?.cliente?.username}</p>
            <p className="text-xs text-slate-400 mt-3">Plan</p>
            <p className="text-white font-semibold">{renovarModal.data?.plan?.nombre}</p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div className="relative">
              <input type="checkbox" className="sr-only" checked={renovarPagoPendiente} onChange={(e) => setRenovarPagoPendiente(e.target.checked)} />
              <div className={`w-10 h-5 rounded-full transition ${renovarPagoPendiente ? "bg-amber-500" : "bg-slate-700"}`} />
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${renovarPagoPendiente ? "translate-x-5" : ""}`} />
            </div>
            <span className="text-sm text-slate-300">
              Renovar con <span className="text-amber-300 font-medium">pago pendiente</span>
            </span>
          </label>

          <p className="text-xs text-slate-500">
            La nueva suscripción arrancará desde hoy según la duración del plan.
          </p>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button variant="ghost" onClick={renovarModal.close}>Cancelar</Button>
            <Button variant="primary" onClick={handleRenovarSubmit} primaryColor={primaryColor}>
              Confirmar renovación
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
